import { Copy, Download } from "lucide-react";
import { useState } from "react";
import { ReactComponent as TransactionSuccessIcon } from "../../assets/img/transaction-success-icon.svg";
import { toast } from "react-toastify";
import { downloadInvoiceFile } from "../../helper/downloadHelper";
import { Body, Title } from "../UIKits/Typography/Typography";
import Button from "../UIKits/Button/Button";
import { formatCurrency } from "../../helper/helper";
import { remitaPaymentService } from "../../services/payment";
import { usePowerPurchaseStore } from "../../store/buyPowerStore";
import { PaymentMethod } from "../Utils/constants";
import { downloadInvoice, getTransactionId, verifyAndGenerateToken } from "../../services/selfService";

interface InvoiceStepProps {
  onClose: () => void;
  onPaymentSuccess: () => void;
  onPaymentError: () => void;
}

export const InvoiceStep: React.FC<InvoiceStepProps> = ({ onClose, onPaymentSuccess, onPaymentError }) => {
  const { invoiceData, meterDetails, amount, triggerRefetch, setPaymentResult, nextStep, setLoading } =
    usePowerPurchaseStore();
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handlePayOnline = async () => {
    if (!invoiceData) return;

    setIsProcessingPayment(true);
    setLoading(true);
    onClose();

    try {
      let payload = {
        transactionId: invoiceData.transactionId as string,
        amount: invoiceData.amount,
        meterNo: meterDetails.meterNumber,
        paymentMethod: PaymentMethod.Remita,
        invoiceNumber: invoiceData.invoiceNumber,
        remitaPaymentResponse: "",
      };
      // before initializing payment, generate transaction Id
      const transactionIdResponse = await getTransactionId({
        invoiceNumber: invoiceData.invoiceNumber,
        transactionId: invoiceData.transactionId as string,
      });
      if (!transactionIdResponse?.data?.isSuccessful || !transactionIdResponse?.data?.data) {
        toast.error("Failed to generate transaction ID. Please try again.");
        setIsProcessingPayment(false);
        setLoading(false);
        return;
      }

      const transactionId = transactionIdResponse.data.data;
      payload.transactionId = transactionId;

      // Initialize Remita payment
      await remitaPaymentService.initializePayment(
        {
          key: "", // From env
          rrr: invoiceData.rrr,
          transactionId,
          amount,
        },
        {
          onSuccess: async (remitaResponse) => {
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndGenerateToken({
               ...payload
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
              const verificationResponseData = verificationResponse.data.data;
              setPaymentResult({
                token: verificationResponseData?.token,
                amount: verificationResponseData.amount,
                equivalentUnit: verificationResponseData.equivalentUnit,
                paymentDate: verificationResponseData.paymentDate,
                email: verificationResponseData.email,
                receiptNumber: verificationResponseData?.receiptNumber,
              });

              onPaymentSuccess();
              toast.success("Payment successful!");
            } else {
              toast.error("Payment verification failed");
              onPaymentError();
            }
            triggerRefetch();
          },
          onError: async (remitaResponse) => {
            toast.error(remitaResponse.message || "Payment failed");
            setIsProcessingPayment(false);
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;
            const verificationResponse = await verifyAndGenerateToken({
               ...payload
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
              const verificationResponseData = verificationResponse.data.data;
              //revert set payment result when remita payment fails, we will just display error modal only
              setPaymentResult({
                token: verificationResponseData?.token,
                amount: verificationResponseData.amount,
                equivalentUnit: verificationResponseData.equivalentUnit,
                paymentDate: verificationResponseData.paymentDate,
                email: verificationResponseData.email,
                receiptNumber: verificationResponseData?.receiptNumber,
              });


              onPaymentError();
            }else {
              // If verification also fails, just call onPaymentError without setting payment result
              onPaymentError();
            }
          },

          onClose: () => {
            setIsProcessingPayment(false);
            setLoading(false);
          },
        }
      );
    } catch (error: any) {
      const errorMessage = error.data?.message || error.message;
      toast.error(errorMessage || "Failed to initialize payment");;
      setIsProcessingPayment(false);
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber: string) => {
    setDownloadingInvoice(true);

    try {
      const response = await downloadInvoice(invoiceNumber);

      if (response.data) {
        // This handles both base64 and URL automatically!
        downloadInvoiceFile(response.data, invoiceNumber, "invoice");

        toast.success("Invoice downloaded successfully");
      }
    } catch (error: any) {
      toast.error(error.data?.message || "Failed to download invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (!invoiceData) return null;

  return (
    <div className=" ">
      {/* Success Icon */}
      <div className="flex justify-center mb-4">
        <div className="bg-primary-100 rounded-full flex items-center justify-center">
          <TransactionSuccessIcon classname="!w-10 !h-10 !md:w-16 !md:h-16" />
          {/* <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-green-500 stroke-[3]" />
          </div> */}
        </div>
      </div>

      <Title level={3} align="center">
        Invoice generated successful
      </Title>

      {/* Invoice Details */}
      <div className="bg-gray-50 rounded-xl p-4 mb-8">
        <div className="flex justify-between items-center my-3 ">
          <Body color="tertiary">Invoice number</Body>
          <div className="flex items-center gap-2">
            <Body>{invoiceData.invoiceNumber}</Body>
            <button
              onClick={() => copyToClipboard(invoiceData.invoiceNumber, "Invoice number")}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center my-3">
          <Body color="tertiary">RRR</Body>
          <div className="flex items-center gap-2">
            <Body>{invoiceData.rrr}</Body>
            <button
              onClick={() => copyToClipboard(invoiceData.rrr, "RRR")}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center my-3">
          <Body color="tertiary">Amount</Body>
          <Body>{formatCurrency(amount)}</Body>
        </div>

        <div className="flex justify-between items-center my-3">
          <Body color="tertiary">Equivalent unit</Body>
          <Body>{invoiceData.equivalentUnit}</Body>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex  gap-4 mt-8">
        {/* <button className="w-full py-3 text-sm font-medium text-foreground-tertiary hover:text-foreground border border-gray-200 rounded-lg transition-colors">
          <Download className="w-4 h-4 inline mr-2" />
          Download invoice
          </button> */}

        <Button variant="outline" className="!w-full" onClick={() => handleDownloadInvoice(invoiceData.invoiceNumber)}>
          <div className="flex items-center justify-center">
            <Download className=" mr-2 w-4 h-4" />

            <p> {downloadingInvoice ? "Downloading..." : "Download Invoice"}</p>
          </div>
        </Button>
        <Button
          className="!w-full "
          loading={isProcessingPayment}
          onClick={handlePayOnline}
          disabled={isProcessingPayment}
        >
          Pay online
        </Button>
      </div>
    </div>
  );
};
export default InvoiceStep;
